<script>
import {
  iButtonsGroup,
  iFeaturedIcon,
  iFeaturedIconSizes,
  iFeaturedIconTypes,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iSwitch,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';

export default {
  components: { iButtonsGroup, iFeaturedIcon, iButton, iSwitch, iTooltip },
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: 'medium-file-search',
    },
    iconType: {
      type: String,
      default: iFeaturedIconTypes.PRIMARY,
    },
    tooltipToggleOn: {
      type: String,
      default: null,
    },
    tooltipToggleOff: {
      type: String,
      default: null,
    },
    tooltipEdit: {
      type: String,
      default: null,
    },
    tooltipDelete: {
      type: String,
      default: null,
    },
    displayTools: {
      type: Boolean,
      default: true,
    },
    switchVisible: { type: Boolean, default: false },
    switchValue: { type: Boolean, default: false },
    withHoverState: { type: Boolean, default: true },
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      iFeaturedIconTypes,
      iFeaturedIconSizes,
      iTooltipAlignmentMode,
      enabledInitialValue: null,
      cardId: crypto.randomUUID(),
    };
  },
  mounted() {
    this.enabledInitialValue = this.switchValue;
  },
  methods: {
    onEditClick() {
      this.$emit('edit-click', { item: this.item });
    },
    onDeleteClick() {
      this.$emit('delete-click', { item: this.item });
    },
    onSwitchChange(value) {
      this.$emit('switch-change', { item: this.item, value });
    },
    onCardClick(value) {
      this.$emit('card-click', { item: this.item, value });
    },
  },
};
</script>

<template>
  <div
    :class="{
      'with-hover-state': withHoverState,
      primary: iconType === iFeaturedIconTypes.PRIMARY,
      error: iconType === iFeaturedIconTypes.ERROR,
    }"
    class="decision-engine-card d-flex flex-row align-items-center i-px-10"
    @click="onCardClick"
  >
    <i-featured-icon
      :symbol="icon"
      :size="iFeaturedIconSizes.XL"
      :type="iconType"
      class="i-mr-8"
    ></i-featured-icon>
    <div class="text d-flex flex-column flex-grow-1 i-gap-1">
      <div class="title i-text-heading-6 i-text-neutral-120">
        {{ title }}
      </div>
      <div v-if="subtitle" class="subtitle i-text-body-2 i-text-neutral-110">
        {{ subtitle }}
      </div>
    </div>
    <div class="controls d-flex flex-grow align-items-center i-gap-10">
      <div
        v-if="switchVisible && !enabledInitialValue"
        class="disabled-tag d-flex flex-grow align-items-center i-text-subheading-3 i-px-3 i-radius-sm"
      >
        Disabled
      </div>
      <i-switch
        v-if="switchVisible && enabledInitialValue !== null"
        :id="'card-switch-' + cardId"
        v-model="enabledInitialValue"
        size="sm"
        @input="onSwitchChange"
      />
      <i-tooltip
        v-if="tooltipToggleOn && tooltipToggleOff"
        :target="`#${'card-switch-' + cardId}`"
        :title="enabledInitialValue ? tooltipToggleOn : tooltipToggleOff"
        :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
      />
      <i-buttons-group v-if="displayTools" class="i-mb-2" type="outline">
        <template #buttons>
          <i-button
            :id="'card-edit-' + cardId"
            :left-icon="'medium-edit-pencil'"
            data-testid="card-button-edit"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.OUTLINE"
            @button-click="onEditClick"
          />
          <i-button
            :id="'card-delete-' + cardId"
            :left-icon="'medium-delete-bin-1'"
            data-testid="card-button-delete"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.OUTLINE"
            @button-click="onDeleteClick"
          />
        </template>
      </i-buttons-group>
      <i-tooltip
        v-if="tooltipEdit && displayTools"
        :target="`#${'card-edit-' + cardId}`"
        :title="tooltipEdit"
        :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
      />
      <i-tooltip
        v-if="tooltipDelete && displayTools"
        :target="`#${'card-delete-' + cardId}`"
        :title="tooltipDelete"
        :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.decision-engine-card {
  height: 88px;
  box-sizing: border-box;
  transition: background-color 150ms ease-out;

  &.with-hover-state {
    cursor: pointer;

    &:hover {
      background: $light-neutral-20;
    }

    &.primary {
      &:hover {
        .i-featured-icon {
          background: $light-primary-40 !important;
        }
      }
    }

    &.error {
      &:hover {
        .i-featured-icon {
          background: $light-error-40 !important;
        }
      }
    }
  }

  .i-featured-icon {
    transition: background-color 150ms ease-out;
  }

  .disabled-tag {
    height: 20px;
    background: $light-neutral-20;
  }
}
</style>
